import Link from 'next/link';
import Image from 'next/image';
import { useState } from 'react';

import SocialLinks from './SocialLinks';
import SignUpForm from './SignUpForm';
import clsx from 'clsx';

const FooterColumn = ({ children }: { children: React.ReactElement }) => {
  return <div className="flex flex-col flex-grow my-3">{children}</div>;
};
interface FooterLinkProps {
  href: string;
  label: string;
}

interface FooterTitleProps {
  headline: string;
  children?: React.ReactNode;
}

const FooterTitle = ({ headline, children }: FooterTitleProps) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="p-2">
        <a className="flex lg:hidden uppercase cursor-pointer font-semibold justify-between"
          onClick={() => setOpen(!open)}>
          <span>{headline}</span>
          <div
            className="lg:hidden px-2"
          >
            <Image
              alt={!open ? 'open-icon' : 'close-icon'}
              width={16}
              height={16}
              src={!open ? '/icons/plus.svg' : '/icons/minus.svg'}
            />
          </div>
        </a>
        <a className="hidden lg:flex uppercase font-semibold justify-between">
          {headline}
        </a>
      </div>
      <div
        className={`flex flex-col transition duration-300 ease-in-out ${open ? 'h-full overflow-auto' : 'h-0 overflow-hidden'
          }`}
      >
        {children}
      </div>
    </>
  );
};

const FooterLink = ({ href, label }: FooterLinkProps) => {
  return (
    <Link href={href} passHref>
      <a className="transition duration-300 rounded text-sm p-2 hover:underline">
        {label}
      </a>
    </Link>
  );
};

const LegalSection = ({ className }: { className?: string }) => {
  const year = new Date().getFullYear();
  return (
    <div className={clsx(className, "flex flex-col lg:ml-4 text-sm text-left p-2")}>
      <div className="flex flex-wrap gap-2">
        <Link href="/privacy-policy" passHref>
          <a>Privacy &amp; Cookies Policy</a>
        </Link>
        <div className="pl-2 border-l">
          <Link href="/accessibility" passHref>
            <a>Accessibility</a>
          </Link>
        </div>
      </div>
      <p className="flex italic mt-2 text-sm">
        &copy; {year} Daily Polished - All Rights Reserved
      </p>
    </div>
  )
}

export default function Footer(): React.ReactElement {

  return (
    <footer className="p-6 bg-onyx text-cloud z-[99]">
      <div className="flex flex-col lg:flex-row mx-auto items-start justify-between gap-10 container">
        <div className="mt-4 lg:my-4">
          <Link href="/" passHref>
            <a>
              <Image src="/daily-polished-white.svg" width={226} height={108} alt="Daily Polished logo" />
            </a>
          </Link>
          <Link href="https://www.producthunt.com/posts/daily-polished?utm_source=badge-featured&utm_medium=badge&utm_souce=badge-daily&#0045;polished" target="_blank" passHref>
            <a className="block mt-5">
              <Image
                src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=394195&theme=neutral"
                alt="Daily Polished - Full size beauty at sample size prices | Product Hunt"
                width={250}
                height={54}
              />
            </a>
          </Link>
        </div>
        <div className="flex flex-col w-full">
          <div className="flex flex-col lg:flex-row justify-between w-full ml-0 lg:ml-4">
            <div className="w-full">
              <div className="flex flex-col lg:flex-row w-full ml-0 lg:ml-4">
                <hr className="ml-2 mt-8 lg:hidden" />
                <FooterColumn>
                  <>
                    <FooterTitle headline="explore">
                      <FooterLink href="/about" label="About Us" />
                      <FooterLink href="/membership" label="Membership" />
                      <FooterLink href="/blog" label="Blog" />
                    </FooterTitle>
                    <div className="hidden lg:flex lg:flex-col">
                      <FooterLink href="/about" label="About Us" />
                      <FooterLink href="/membership" label="Membership" />
                      <FooterLink href="/blog" label="Blog" />
                    </div>
                  </>
                </FooterColumn>
                <hr className="ml-2 lg:hidden" />
                <FooterColumn>
                  <>
                    <FooterTitle headline="Customer Service">
                      <FooterLink href="/faq" label="FAQs" />
                      <FooterLink href="/contact" label="Contact" />
                      <FooterLink
                        href="/terms-and-conditions"
                        label="Membership Terms"
                      />
                    </FooterTitle>
                    <div className="hidden lg:flex lg:flex-col">
                      <FooterLink href="/faq" label="FAQs" />
                      <FooterLink href="/contact" label="Contact" />
                      <FooterLink
                        href="/terms-and-conditions"
                        label="Membership Terms"
                      />
                    </div>
                  </>
                </FooterColumn>
                <hr className="ml-2 lg:hidden" />
                <FooterColumn>
                  <>
                    <FooterTitle headline="For Brands">
                      <FooterLink
                        href="/brand-application"
                        label="Brand Application"
                      />
                    </FooterTitle>
                    <div className="hidden lg:flex lg:flex-col">
                      <FooterLink
                        href="/brand-application"
                        label="Brand Application"
                      />
                    </div>
                  </>
                </FooterColumn>
              </div>
              <LegalSection className="mt-5 hidden lg:block" />
            </div>
            <hr className="ml-2 lg:hidden" />
            <div className="order-first lg:order-last">
              <div className="flex flex-col lg:my-4">
                <p className="font-bold uppercase p-2">Sign&nbsp;up</p>
                <p className="mt-3 pl-2 text-sm">
                  Sign up to get exclusive news and offers,
                  <br />
                  and discover the world of Daily Polished
                </p>
                <SignUpForm />
              </div>
              <div className="hidden lg:flex lg:flex-col px-2">
                <SocialLinks />
              </div>
            </div>
            <div className="flex lg:hidden my-6 px-2">
              <SocialLinks />
            </div>
          </div>
          <LegalSection className="lg:hidden" />
        </div>
      </div>
    </footer>
  );
}
